import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import type { ChipProps } from './chip.types';

const chipVariants = cva('inline-flex items-center gap-1.5 rounded-full font-medium transition-colors', {
    variants: {
        variant: {
            filled: 'bg-[var(--color-primary-20)] text-[var(--color-primary-100)]',
            outlined: 'border-2 border-[var(--color-primary-100)] bg-transparent text-[var(--color-primary-100)]',
        },
        size: {
            small: 'h-6 px-2 text-xs',
            medium: 'h-8 px-3 text-sm',
            large: 'h-10 px-4 text-base',
        },
        disabled: {
            true: 'opacity-50 cursor-not-allowed',
            false: 'cursor-pointer hover:opacity-80',
        },
    },
    defaultVariants: {
        variant: 'filled',
        size: 'medium',
        disabled: false,
    },
});

const Chip: React.FC<ChipProps> = ({
    label,
    variant = 'filled',
    size = 'medium',
    disabled = false,
    icon,
    onPress,
    onClose,
    closeIcon = false,
    className,
    children,
    ...props
}) => {
    return (
        <div
            data-testid="chip"
            className={cn(chipVariants({ variant, size, disabled, className }))}
            onClick={!disabled ? onPress : undefined}
            role={onPress ? 'button' : undefined}
            tabIndex={!disabled && onPress ? 0 : undefined}
            {...props}
        >
            {icon && <span className="flex-shrink-0">{icon}</span>}
            <span>{label || children}</span>
            {closeIcon && onClose && (
                <button
                    type="button"
                    onClick={e => {
                        e.stopPropagation();
                        if (!disabled) onClose();
                    }}
                    className="flex-shrink-0 hover:opacity-70"
                    aria-label="Close"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>
            )}
        </div>
    );
};

export default Chip;
